<template>
  <div class="bg-white dark:bg-gray-800/50 rounded-lg shadow overflow-hidden">
    <div class="px-3 sm:px-4 py-2 sm:py-3 border-b border-gray-200 dark:border-gray-700">
      <div class="flex items-center gap-2">
        <div class="p-1.5 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg">
          <svg
            class="w-5 h-5 text-emerald-600 dark:text-emerald-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
            />
          </svg>
        </div>
        <div>
          <h2 class="text-base sm:text-lg font-bold text-gray-900 dark:text-white">
            Métodos de Pago
          </h2>
          <p class="text-xs text-gray-600 dark:text-gray-400">
            Configura los métodos de pago disponibles
          </p>
        </div>
      </div>
    </div>

    <div class="p-3 sm:p-4">
      <PaymentMethodToggleList
        :is-saving="isSaving"
        :payment-methods-config="paymentMethodsConfig"
        :payment-method-items="paymentMethodItems"
        @toggle="restaurantSettings.togglePaymentMethod"
      />

      <CustomPaymentMethodsManager
        :is-saving="isSaving"
        :custom-methods="paymentMethodsConfig.custom_methods || []"
        @toggle="restaurantSettings.toggleCustomPaymentMethod"
        @remove="restaurantSettings.removeCustomPaymentMethod"
        @add="handleAddCustomMethod"
        @update="handleUpdateCustomMethod"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import type { useRestaurantSettings } from '@/composables/useRestaurantSettings'
import PaymentMethodToggleList from '@/components/configuration/PaymentMethodToggleList.vue'
import CustomPaymentMethodsManager from '@/components/configuration/CustomPaymentMethodsManager.vue'

type PaymentMethodKey = 'card' | 'digital' | 'other'

const props = defineProps<{
  restaurantSettings: ReturnType<typeof useRestaurantSettings>
}>()

const restaurantSettings = props.restaurantSettings

const isSaving = computed(() => restaurantSettings.savingSettings.value)
const paymentMethodsConfig = computed(() => restaurantSettings.paymentMethodsConfig.value)

const paymentMethodItems: Array<{ key: PaymentMethodKey; labelKey: string; iconPath: string }> = [
  {
    key: 'card',
    labelKey: 'app.subscription.settings.payment_methods.card',
    iconPath: 'M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z'
  },
  {
    key: 'digital',
    labelKey: 'app.subscription.settings.payment_methods.digital',
    iconPath: 'M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z'
  },
  {
    key: 'other',
    labelKey: 'app.subscription.settings.payment_methods.other',
    iconPath: 'M12 6v6m0 0v6m0-6h6m-6 0H6'
  }
]

const handleAddCustomMethod = async (methodName: string) => {
  try {
    await restaurantSettings.addCustomPaymentMethod(methodName)
  } catch (error) {
    console.error('Error adding custom payment method:', error)
  }
}

const handleUpdateCustomMethod = async (payload: { oldName: string; newName: string }) => {
  try {
    await restaurantSettings.updateCustomPaymentMethodName(payload.oldName, payload.newName)
  } catch (error) {
    console.error('Error updating custom payment method:', error)
  }
}
</script>
